import { useCallback, useEffect, useState } from "react";
import { Zap, ListOrdered } from "lucide-react";
import api from "@/lib/api";
import { useI18n } from "@/context/I18nContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { InlineLoader } from "@/components/common/Loader";
import { OPS } from "@/constants/testIds";
import { WmBranchSelect } from "@/components/waste-meals/WmBranchSelect";
import { QuickEntryForm } from "@/components/waste-meals/QuickEntryForm";
import { DaySheetForm } from "@/components/waste-meals/DaySheetForm";
import { MyEntries } from "@/components/waste-meals/MyEntries";
import { NoActivityButton } from "@/components/waste-meals/NoActivityButton";
import { BeneficiariesField } from "@/components/waste-meals/BeneficiariesField";

const C = OPS.wm;

/**
 * One entry tab. The two tabs («تسجيل الهدر» and «أكل العمال») are the same
 * screen with `type` fixed by the tab — the type is never a toggle inside the
 * form, which is how the manager's own menu is organised.
 */
export function EntryTab({ scope, type }) {
    const { t } = useI18n();
    const [branchId, setBranchId] = useState(scope?.default_branch_id || "");
    const [date, setDate] = useState(scope?.default_date || scope?.today || "");
    const [mode, setMode] = useState("quick");
    const [catalogue, setCatalogue] = useState(null);
    const [day, setDay] = useState(null);
    const [loading, setLoading] = useState(true);
    const [reloadKey, setReloadKey] = useState(0);

    const load = useCallback(async () => {
        if (!branchId || !date) return;
        setLoading(true);
        try {
            const [cat, dayRes] = await Promise.all([
                api.get("/waste-meals/catalogue", { params: { type, branch_id: branchId } }),
                api.get("/waste-meals/day", { params: { type, branch_id: branchId, date } }),
            ]);
            setCatalogue(cat.data);
            setDay(dayRes.data);
        } finally {
            setLoading(false);
        }
    }, [branchId, date, type]);

    useEffect(() => { load(); }, [load]);

    const refresh = async () => { await load(); setReloadKey((k) => k + 1); };

    const ModeButton = ({ mkey, icon: Icon, label, testId }) => (
        <Button data-testid={testId} size="sm"
                variant={mode === mkey ? "default" : "outline"}
                onClick={() => setMode(mkey)} className="h-9 flex-1 gap-1.5 text-xs font-semibold">
            <Icon className="h-3.5 w-3.5" />
            {label}
        </Button>
    );

    return (
        <div className="space-y-3">
            <div className="grid gap-2 sm:grid-cols-2">
                <WmBranchSelect branches={scope?.branches} value={branchId} onChange={setBranchId} />
                {/* The day sheet is a whole day, so its date belongs in front of
                    the user. Quick add keeps it behind «المزيد» — one date
                    control per screen, never two. */}
                {mode === "sheet" ? (
                    <Input data-testid={C.dateInputTop} type="date" value={date} dir="ltr" lang="ar"
                           min={scope?.min_date || undefined} max={scope?.today}
                           onChange={(e) => setDate(e.target.value)} className="h-9" />
                ) : null}
            </div>

            <div className="flex gap-2">
                <ModeButton mkey="quick" icon={Zap} label={t("wm_mode_quick")} testId={C.modeQuick} />
                <ModeButton mkey="sheet" icon={ListOrdered} label={t("wm_mode_sheet")} testId={C.modeSheet} />
            </div>

            {type === "meals" ? (
                <BeneficiariesField day={day} branchId={branchId} date={date} onSaved={refresh} />
            ) : null}

            {loading && !catalogue ? <InlineLoader /> : mode === "quick" ? (
                <QuickEntryForm scope={scope} catalogue={catalogue} branchId={branchId} date={date}
                                type={type} onDateChange={setDate} onSaved={refresh} />
            ) : (
                <DaySheetForm scope={scope} catalogue={catalogue} day={day} branchId={branchId}
                              date={date} type={type} onSaved={refresh} />
            )}

            <NoActivityButton day={day} branchId={branchId} date={date} type={type} onSaved={refresh} />

            <MyEntries scope={scope} catalogue={catalogue} branchId={branchId} type={type}
                       reloadKey={reloadKey} />
        </div>
    );
}
